import { useState, useEffect, useCallback } from 'react';
import { PositionService } from '../services/positionService';
import { PortfolioService } from '../services/portfolioService';
import { Position, Portfolio } from '../types';
import { PortfolioPerformance, PerformanceMetrics, PerformanceDataPoint } from '../types/performance';
import { differenceInMonths } from 'date-fns';

interface UsePortfolioPerformanceResult {
  performance: PortfolioPerformance | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const usePortfolioPerformance = (portfolioId: string | null): UsePortfolioPerformanceResult => {
  const [performance, setPerformance] = useState<PortfolioPerformance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPerformance = useCallback(async () => {
    if (!portfolioId) {
      setPerformance(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch portfolio and closed positions
      const [portfolio, closedPositions] = await Promise.all([
        PortfolioService.getPortfolio(portfolioId),
        PositionService.getClosedPositions(portfolioId)
      ]);

      if (!portfolio) {
        setError('Portfolio not found');
        return;
      }

      // Sort positions by exit date ascending for cumulative calculation
      const sortedPositions = [...closedPositions].sort((a, b) => {
        const dateA = a.exitDate ? a.exitDate.getTime() : 0;
        const dateB = b.exitDate ? b.exitDate.getTime() : 0;
        return dateA - dateB;
      });

      // Calculate data points and metrics
      const dataPoints = buildDataPoints(sortedPositions, portfolio.initialFundValue);
      const metrics = calculateMetrics(sortedPositions, portfolio);

      setPerformance({
        dataPoints,
        metrics,
        portfolioId: portfolio.id,
        portfolioName: portfolio.name,
        initialFundValue: portfolio.initialFundValue
      });
    } catch (err) {
      console.error('Error fetching portfolio performance:', err);
      setError('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  return { performance, loading, error, refetch: fetchPerformance };
};

function buildDataPoints(positions: Position[], initialFundValue: number): PerformanceDataPoint[] {
  const dataPoints: PerformanceDataPoint[] = [];
  let cumulativeReturn = 0;
  let tradeCount = 0;

  // Add starting point at zero
  if (positions.length > 0 && positions[0].exitDate) {
    dataPoints.push({
      date: new Date(positions[0].exitDate.getTime() - 86400000), // Day before first trade
      cumulativeReturn: 0,
      cumulativeReturnPercent: 0,
      tradeCount: 0
    });
  }

  for (const position of positions) {
    if (!position.exitDate || position.realizedGainLoss === undefined) continue;

    cumulativeReturn += position.realizedGainLoss;
    tradeCount++;

    dataPoints.push({
      date: position.exitDate,
      cumulativeReturn,
      cumulativeReturnPercent: (cumulativeReturn / initialFundValue) * 100,
      tradeCount
    });
  }

  return dataPoints;
}

function calculateMetrics(positions: Position[], portfolio: Portfolio): PerformanceMetrics {
  // Filter positions with valid realized gain/loss
  const validPositions = positions.filter(p =>
    p.realizedGainLoss !== undefined && p.exitDate
  );

  const totalTrades = validPositions.length;

  if (totalTrades === 0) {
    return {
      totalReturn: 0,
      totalReturnPercent: 0,
      avgMonthlyReturn: 0,
      avgMonthlyReturnPercent: 0,
      winRate: 0,
      winCount: 0,
      lossCount: 0,
      profitFactor: null,
      expectancy: 0,
      maxDrawdown: 0,
      maxDrawdownPercent: 0,
      monthsActive: 0,
      totalTrades: 0,
      avgWin: 0,
      avgLoss: 0
    };
  }

  // Calculate wins and losses
  const wins = validPositions.filter(p => (p.realizedGainLoss || 0) > 0);
  const losses = validPositions.filter(p => (p.realizedGainLoss || 0) <= 0);

  const winCount = wins.length;
  const lossCount = losses.length;

  const grossProfit = wins.reduce((sum, p) => sum + (p.realizedGainLoss || 0), 0);
  const grossLoss = Math.abs(losses.reduce((sum, p) => sum + (p.realizedGainLoss || 0), 0));

  const totalReturn = grossProfit - grossLoss;
  const totalReturnPercent = (totalReturn / portfolio.initialFundValue) * 100;

  // Win rate
  const winRate = (winCount / totalTrades) * 100;

  // Average win and loss
  const avgWin = winCount > 0 ? grossProfit / winCount : 0;
  const avgLoss = lossCount > 0 ? grossLoss / lossCount : 0;

  // Profit factor (null if no losses to avoid division by zero)
  const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : null;

  // Expectancy: (Win% × Avg Win) - (Loss% × Avg Loss)
  const winPercent = winCount / totalTrades;
  const lossPercent = lossCount / totalTrades;
  const expectancy = (winPercent * avgWin) - (lossPercent * avgLoss);

  // Max drawdown calculation
  let peak = 0;
  let maxDrawdown = 0;
  let cumulative = 0;

  for (const position of validPositions) {
    cumulative += position.realizedGainLoss || 0;
    if (cumulative > peak) {
      peak = cumulative;
    }
    const drawdown = peak - cumulative;
    if (drawdown > maxDrawdown) {
      maxDrawdown = drawdown;
    }
  }

  const maxDrawdownPercent = portfolio.initialFundValue > 0
    ? (maxDrawdown / portfolio.initialFundValue) * 100
    : 0;

  // Months active calculation
  const firstExitDate = validPositions[0]?.exitDate;
  const lastExitDate = validPositions[validPositions.length - 1]?.exitDate;

  let monthsActive = 1; // Minimum 1 month to avoid division by zero
  if (firstExitDate && lastExitDate) {
    const months = differenceInMonths(lastExitDate, firstExitDate);
    monthsActive = Math.max(1, months + 1); // +1 to include partial months
  }

  // Average monthly return
  const avgMonthlyReturn = totalReturn / monthsActive;
  const avgMonthlyReturnPercent = (avgMonthlyReturn / portfolio.initialFundValue) * 100;

  return {
    totalReturn,
    totalReturnPercent,
    avgMonthlyReturn,
    avgMonthlyReturnPercent,
    winRate,
    winCount,
    lossCount,
    profitFactor,
    expectancy,
    maxDrawdown,
    maxDrawdownPercent,
    monthsActive,
    totalTrades,
    avgWin,
    avgLoss
  };
}
